import React from 'react';
import type { FileRejection } from 'react-dropzone';
import { DropzoneWrapper } from '@/components/elements/DropzoneWrapper';
import {
	CTAScreen,
	DropImage,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import { Button } from '@/components/UI';
import { useTranslationContext } from '@/context/translation';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
	useTranslationWithLocale,
	useUploadImage,
} from '@/hooks';
import {
	AmplitudeService,
	CookiesStorageService,
	GenerateLandingLinkService,
	GoogleService,
} from '@/services';
import {
	benefitsEnhancement,
	funnelReviewsEnhancement,
	howItWorkEnhancement,
	reviewsLanding,
	whyUseEnhancement,
} from '@/data';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LANDING_CONSTANTS,
	LANDING_COOKIE_STORAGE,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import benefit from '@/public/images/image-enhancement/benefits.webp';
import funnelImg1 from '@/public/images/image-enhancement/funnelImg1.webp';
import funnelImg2 from '@/public/images/image-enhancement/funnelImg2.webp';
import howItWork from '@/public/images/image-enhancement/howItWorks.webp';
import whyUse from '@/public/images/image-enhancement/whyUse.webp';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import styles from './ImageEnhancementLanding.module.scss';

interface Props {}
const ImageEnhancementLanding: React.FC<Props> = () => {
	// Translations
	const { locale, translations } = useTranslationContext();
	const { t } = useTranslationWithLocale(
		'image-enhancement',
		locale,
		translations?.['image-enhancement'],
	);
	const { t: tReviews } = useTranslationWithLocale(
		'landing-reviews',
		locale,
		translations?.['landing-reviews'],
	);

	// States
	const [loading, setLoading] = React.useState(false);

	// Logic
	const uploadImage = useUploadImage();

	// Helpers
	const isLoading = loading || uploadImage.isLoading;

	// Actions
	const handleDrop = (
		acceptedFiles: File[],
		fileRejections: FileRejection[],
	) => {
		if (fileRejections.length > 0) {
			console.warn('Rejected files:', fileRejections);
			return;
		}

		setLoading(true);
		uploadImage.mutate(
			{ file: acceptedFiles[0] },
			{
				onSuccess: async ({ viewUrl }) => {
					CookiesStorageService.set(
						LANDING_COOKIE_STORAGE.IMAGE_FROM_LANDING,
						viewUrl,
					);
					GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
					await AmplitudeService.logEventAsync(AMPLITUDE_EVENTS.MEDIA_ADDED, {
						context: LANDING_AMPLITUDE_EVENTS.IMAGE_ENHANCER_LANDING,
					});
					setLoading(false);

					window.location.href =
						GenerateLandingLinkService.funnelImageEnhancement();
				},
				onError: () => {
					setLoading(false);
				},
			},
		);
	};
	const openGalleryEvent = () => {
		AmplitudeService.logEvent(AMPLITUDE_EVENTS.MEDIA_GALLERY_SHOWN, {
			context: LANDING_AMPLITUDE_EVENTS.IMAGE_ENHANCER_LANDING,
		});
	};

	// Analytics
	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.IMAGE_ENHANCE_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.IMAGE_ENHANCEMENT },
	);
	useLandingShownEvent({ type: LANDING_AMPLITUDE_EVENTS.IMAGE_ENHANCEMENT });

	return (
		<>
			<DropImage
				imageLeft={{
					src: funnelImg1,
					alt: 'enhancement preview',
				}}
				imageRight={{
					src: funnelImg2,
					alt: 'enhancement preview',
				}}
				t={t}
				isLoading={isLoading}
				dropzoneOptions={{
					disabled: isLoading,
					multiple: false,
					accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
					maxFiles: LANDING_CONSTANTS.MAX_FILES,
					maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
					onDrop: handleDrop,
					onFileDialogOpen: openGalleryEvent,
				}}
				title={t('heroData.title')}
				subtitle={t('heroData.subtitle')}
				reviewsData={funnelReviewsEnhancement}
				classes={{
					dropdownClass:
						styles['imageEnhancementLanding__drop-image-container'],
				}}
			/>

			{/* HowItWork */}
			<ImageTextSection
				title={t('howItWorkData.mainTitle')}
				image={howItWork}
				data={howItWorkEnhancement}
				t={t}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['imageEnhancementLanding__drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* Benefit */}
			<ImageTextSection
				title={t('benefitData.mainTitle')}
				image={benefit}
				specialListItem
				data={benefitsEnhancement}
				t={t}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['imageEnhancementLanding__drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>

			{/* WhyUse */}
			<ImageTextSection
				title={t('whyUseData.mainTitle')}
				image={whyUse}
				data={whyUseEnhancement}
				t={t}
				classes={{
					imageContainer:
						styles['imageEnhancementLanding__whyUse-image-container'],
					list: styles['imageEnhancementLanding__whyUse-list'],
				}}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['imageEnhancementLanding__drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData.title1')}
				title_green={t('tryLiftData.greenText')}
				title2={t('tryLiftData.title2')}
				subtitle={t('tryLiftData.subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData.altText')}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing_outline'
							text={t('getStarted')}
							size='large'
							classes={{
								button:
									styles['imageEnhancementLanding__cta-drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>
		</>
	);
};

export default ImageEnhancementLanding;
